import React, { useContext, useState, useEffect, useMemo } from 'react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import Custom from '@/components/pages/custom/Custom';
import { DataContext } from '../data-provider/DataProvider';
import Schedule from '@/components/pages/schedule/Schedule';
import Updates from '@/components/pages/updates/Updates';
import { MOBILE_RESPONSIVE } from '../../../components/ui/main/Main';
import { calculatePageBgStyles } from '../utils';
import { DEFAULT_PAGE_NAMES, pageIds } from '../../../constants';
import { RouterState, useResponsiveValue } from '@monash/portal-react';
import GenericError from '@/components/utilities/error/GenericError';
import { getPageIdFromRoute } from '@/components/utilities/pages/get-page-id-from-route';
import {
  ImpersonationContext,
  useSessionStorage,
} from '@monash/portal-frontend-common';
import { transformPageNameForURL } from '@/components/utilities/pages';
import { handleToggleForDefaultPage } from './handle-toggle-for-default-page';
import { usePagesStore } from '@/stores/pages';
import { UpcomingContainer } from '@/components/pages/upcoming/UpcomingContainer';
import { isBannerRelevantToUser, useGlobalBannersData } from './utils';
import { checkShouldRender } from './utils/check-should-render';

export const PageContext = React.createContext({});

const PageProvider = ({ children }) => {
  const { portalPreferences, updatePortalPreferences, currentDate } =
    useContext(DataContext);
  const { currentUser } = useContext(ImpersonationContext);
  const { UPCOMING, SCHEDULE, UPDATES } = pageIds;
  const { route } = useContext(RouterState);

  const [showGlobalBanner] = useSessionStorage('mock:showGlobalBanner');
  const [numberOfBanners] = useSessionStorage('mock:numberOfGlobalBanners');

  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S';

  const pagesData = useMemo(() => {
    return (
      portalPreferences?.pages &&
      handleToggleForDefaultPage({
        currentPages: portalPreferences?.pages,
        showPage: {
          [UPDATES]: !currentUser?.is?.MonashCollege,
        },
      })
    );
  }, [portalPreferences]);

  const hiddenPages = pagesData?.hiddenPages;

  const selectedPageId = usePagesStore((state) => state.selectedPageId);
  const setSelectedPageId = usePagesStore((state) => state.setSelectedPageId);

  // states
  const [bgRefHandler, setBgRefHandler] = useState();
  const [backgroundStyle, setBackgroundStyle] = useState();
  const [isMobileDropDownExpanded, setIsMobileDropDownExpanded] =
    useState(false);

  // page animation state for parallax effect
  const [hasPageAnimation, setHasPageAnimation] = useState(
    portalPreferences?.customTheme?.hasPageAnimation ?? true
  );
  // hasUploadedWallpaper to track for transparent wallpaper bug (we insert styling differently)
  const [hasUploadedWallpaper, setHasUploadedWallpaper] = useState(
    Boolean(portalPreferences?.customTheme?.wallpaper)
  );

  const visiblePagesPageOrder =
    pagesData?.pageOrder &&
    [...pagesData?.pageOrder].filter((id) => !hiddenPages?.includes(id));

  const selectedPage = selectedPageId
    ? visiblePagesPageOrder?.findIndex((id) => id === selectedPageId)
    : null;

  // refs
  const pageRefs = useMemo(
    () =>
      visiblePagesPageOrder?.map(() => {
        return React.createRef();
      }),
    [visiblePagesPageOrder]
  );

  // to avoid detecting programmatic scroll, set this to true when using code to cause scroll event (e.g. when calling scrollTo)
  const [isProgrammaticScrolling, setIsProgrammaticScrolling] = useState(false);

  // map pages
  const mapPage = (id, i) => {
    const isSelected = selectedPage === i;
    const shouldRender = checkShouldRender({
      selectedPage,
      currentPageIndex: i,
      isMobile,
    });

    switch (id) {
      case UPCOMING: {
        const name = DEFAULT_PAGE_NAMES[UPCOMING];

        return {
          id,
          name,
          path: transformPageNameForURL(name),
          custom: false,
          page: () => (
            <SentryErrorBoundary fallback={<GenericError />}>
              {shouldRender && (
                <UpcomingContainer i={i} isSelected={isSelected} />
              )}
            </SentryErrorBoundary>
          ),
        };
      }
      case SCHEDULE: {
        const name = DEFAULT_PAGE_NAMES[SCHEDULE];

        return {
          id,
          name,
          path: transformPageNameForURL(name),
          custom: false,
          page: () => (
            <SentryErrorBoundary fallback={<GenericError />}>
              {shouldRender && <Schedule i={i} isSelected={isSelected} />}
            </SentryErrorBoundary>
          ),
        };
      }
      case UPDATES: {
        const name = DEFAULT_PAGE_NAMES[UPDATES];

        return {
          id,
          name,
          path: transformPageNameForURL(name),
          custom: false,
          page: () => (
            <SentryErrorBoundary fallback={<GenericError />}>
              {shouldRender && <Updates i={i} isSelected={isSelected} />}
            </SentryErrorBoundary>
          ),
        };
      }
      default: {
        const customPage = portalPreferences?.pages.customPages[id];

        if (!customPage) {
          return;
        }

        return {
          id,
          name: customPage.name,
          path: `/page/${transformPageNameForURL(customPage.name)}`,
          custom: true,
          page: () => (
            <SentryErrorBoundary fallback={<GenericError />}>
              {shouldRender && (
                <Custom pageId={id} i={i} isSelected={isSelected} />
              )}
            </SentryErrorBoundary>
          ),
        };
      }
    }
  };

  const mappedVisiblePages = visiblePagesPageOrder
    ?.map((id, i) => mapPage(id, i))
    .filter(Boolean);

  const mappedAllPages = pagesData?.pageOrder
    ?.map((id, i) => {
      return {
        ...mapPage(id, i),
        hidden: hiddenPages?.includes(id),
      };
    })
    .filter(Boolean);

  // hidden pages
  const allPagesHidden =
    pagesData &&
    pagesData?.hiddenPages?.length === pagesData?.pageOrder?.length;

  const onHiddenPage = selectedPageId && hiddenPages?.includes(selectedPageId);

  const { globalBannersData } = useGlobalBannersData({
    userIs: currentUser?.is,
  });

  const lastDismissedTime =
    portalPreferences?.globalBanners?.home?.lastDismissed || 0;

  const numberSliced =
    numberOfBanners === 'Many' ? globalBannersData.length : 1;

  // active global banners
  const activeGlobalBanners = showGlobalBanner
    ? globalBannersData.slice(0, numberSliced)
    : globalBannersData.filter(
        (banner) =>
          isBannerRelevantToUser({
            userIs: currentUser?.is,
            bannerCohorts: banner.relevantCohorts,
          }) &&
          lastDismissedTime < banner.startDate &&
          currentDate > banner.startDate &&
          currentDate < banner.endDate
      );

  // global banner should be shown on the first visible page only
  const globalBannerShownPageId =
    activeGlobalBanners.length !== 0 ? visiblePagesPageOrder?.[0] : null;

  useEffect(() => {
    setHasUploadedWallpaper(Boolean(portalPreferences?.customTheme?.wallpaper));
  }, [portalPreferences?.customTheme?.wallpaper]);

  useEffect(() => {
    // if wallpaper upload changes to null, need to adjust state and preferences
    // to put Switch to isOn && disabled
    if (!hasUploadedWallpaper) {
      setHasPageAnimation(true);
      updatePortalPreferences({
        customTheme: {
          ...portalPreferences?.customTheme,
          hasPageAnimation: true,
        },
      });
    }

    setBackgroundStyle(
      calculatePageBgStyles(
        mappedVisiblePages?.length,
        selectedPage,
        hasPageAnimation,
        hasUploadedWallpaper
      )
    );
  }, [
    mappedVisiblePages?.length,
    selectedPage,
    hasPageAnimation,
    hasUploadedWallpaper,
  ]);

  useEffect(() => {
    const pathDirectories = route.path?.split('/');

    if (pagesData) {
      const pageId = getPageIdFromRoute(mappedAllPages, route.path);

      // set selected page id from route if page exists
      if (pageId) {
        setSelectedPageId(pageId);

        // set selected page id to null when user is on a search result page
      } else if (pathDirectories?.[1].startsWith('search')) {
        setSelectedPageId(null);

        //  set selected page id to first visible page id if user is on landing page or on a non existing page
      } else {
        setSelectedPageId(visiblePagesPageOrder?.[0]);
      }
    }
  }, [pagesData, pagesData?.pageOrder, route.path]);

  return (
    <PageContext.Provider
      value={{
        pages: mappedVisiblePages,
        allPages: mappedAllPages,
        allPagesHidden,
        onHiddenPage,
        mapPage,
        pagesData,
        activeGlobalBanners,
        globalBannerShownPageId,
        hasPageAnimation,
        setHasPageAnimation,
        selectedPage,
        selectedPageId,
        visiblePagesPageOrder,
        bgRefHandler,
        setBgRefHandler,
        backgroundStyle,
        pageRefs,
        isProgrammaticScrolling,
        setIsProgrammaticScrolling,
        isExpanded: isMobileDropDownExpanded,
        setIsExpanded: setIsMobileDropDownExpanded,
        hasUploadedWallpaper,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export default PageProvider;
